/**
 * Armado de los textos de la vista previa de la empresa que ve el representante legal
 * (RF-ONB-19, AB#10741/US-7).
 *
 * Vive fuera de las pantallas porque lo consumen dos: la pantalla de inicio del representante y el
 * paso /conformidad-rl, que muestran los mismos datos con distinto layout. Si el texto se duplicara,
 * las dos versiones dirian cosas distintas a la primera correccion de copy.
 *
 * Desde AB#11306 la pantalla de inicio ya no arma un parrafo condensado: pide los tramos por
 * separado (armarBloquesEmpresa) y los pinta en bloques.
 */

import { formatCuit } from '@/utils/services/forms';

/** El CUIT viene sin guiones desde la base; se muestra como lo escribe la gente. */
export const mostrarCuit = (cuit?: string) => (cuit ? formatCuit(cuit) : '');

/**
 * Telefono de contacto como lo va a reconocer la persona, sin los prefijos tecnicos de marcado.
 *
 * El flujo juridico guarda el valor crudo: telefono-juridico/page.tsx manda lo que se tipeo sin
 * pasar por formatPhoneNumber, a diferencia del flujo de persona fisica. Por eso el mismo campo
 * puede llegar como "1123456789" o como "+5491123456789", y el prefijo de pais con el 9 de movil
 * es ruido que estorba para reconocer el numero propio.
 *
 * No se separa el codigo de area a proposito: en Argentina mide 2, 3 o 4 digitos segun la
 * localidad, y partirlo en el lugar equivocado muestra un numero que el representante no reconoce,
 * que es peor que no separarlo. Por la misma razon, un valor que ya esta en forma nacional se
 * devuelve intacto aunque traiga espacios o guiones: el backoffice permite editarlo con formato
 * (`^[0-9+\-\s()]{8,20}$`), y aplanar un "11 2345-6789" que alguien escribio legible seria
 * empeorarlo. Solo se toca lo que tiene prefijo de mas.
 *
 * Todo lo que no termine en los 10 digitos nacionales se devuelve tal como vino: es un dato que
 * existe, y el representante tiene que poder verlo aunque este guardado raro.
 */
export const mostrarTelefono = (telefono?: string) => {
  const original = telefono?.trim();
  if (!original) return '';

  const digitos = original.replace(/\D/g, '');
  if (digitos.length === 10) return original;

  const nacional = digitos.replace(/^54/, '').replace(/^9/, '').replace(/^0/, '');

  return nacional.length === 10 ? nacional : original;
};

export interface Representante {
  id: string;
  cargo?: string;
  documentoEnmascarado?: string;
  emailEnmascarado?: string;
}

export interface Domicilio {
  tipoDomicilio?: string;
  calle?: string;
  numeracion?: string;
  piso?: string;
  departamento?: string;
  localidad?: string;
  provincia?: string;
  codigoPostal?: string;
}

export interface VistaPrevia {
  razonSocial?: string;
  cuit?: string;
  tipoSociedad?: string;
  telefono?: string;
  conformidadRegistrada?: boolean;
  domicilios?: Domicilio[];
  declaracionJurada?: { esFacta?: boolean; esOcde?: boolean; esUif?: boolean };
  representantes?: Representante[];
}

/**
 * Direccion en una linea, salteando los tramos que no vinieron cargados.
 *
 * El separador es parametrico porque los dos usos necesitan distinto: en la lista de datos de
 * /conformidad-rl el punto medio separa visualmente los tramos, pero dentro de un parrafo de texto
 * corrido corta la lectura, y ahi corresponde la coma.
 */
export const armarDireccion = (d: Domicilio, separador = ' · ') =>
  [
    [d.calle, d.numeracion].filter(Boolean).join(' '),
    [d.piso && `Piso ${d.piso}`, d.departamento && `Depto ${d.departamento}`].filter(Boolean).join(' '),
    [d.localidad, d.provincia].filter(Boolean).join(', '),
    d.codigoPostal && `CP ${d.codigoPostal}`,
  ]
    .filter(Boolean)
    .join(separador);

/**
 * Tramos de los datos de la empresa, listos para que la pantalla los pinte en bloques separados
 * (AB#11306). Antes esto era un parrafo unico de texto corrido: el representante tiene que
 * *ratificar* estos datos uno por uno, y en un bloque corrido no se pueden leer de a uno.
 *
 * Devuelve estructura y no JSX para que el copy siga viviendo fuera de las pantallas, que es la
 * razon de ser de este modulo.
 */
export interface BloquesEmpresa {
  encabezado: string;
  /**
   * Los datos de la empresa en una sola linea. La razon social viaja aparte para poder resaltarla
   * sin partir el resto del texto.
   */
  empresa: { razonSocial: string; detalle: string } | null;
  /** Una oracion por declaracion jurada, cada una en su renglon. */
  declaraciones: string[];
  cierre: string;
}

const ENCABEZADO_SIN_DATOS =
  'Estás por validar tu identidad como representante legal de la empresa que te invitó. Revisá los datos antes de continuar.';

const CIERRE = 'Revisá que los datos sean correctos antes de continuar.';

/**
 * Domicilios para la linea unica, sin repetir dos veces la misma direccion.
 *
 * Es comun que el domicilio fiscal y el comercial sean el mismo: imprimirlo dos veces ocupa el
 * doble sin decir nada nuevo, y fue justamente lo que negocio marco como poco compacto. Cuando
 * queda una sola direccion va sin etiqueta, porque no hay nada que distinguir; cuando son distintas
 * cada una lleva su tipo, que ahi si importa. Los dos domicilios se conservan en ese caso: son
 * datos que el representante esta ratificando y no se pueden esconder.
 */
const armarDomicilios = (domicilios?: Domicilio[]) => {
  const conDireccion = (domicilios ?? [])
    .map((domicilio) => ({ tipo: domicilio.tipoDomicilio, direccion: armarDireccion(domicilio, ', ') }))
    .filter((d) => Boolean(d.direccion));

  // Se agrupa por direccion y no por tipo: lo que no hay que repetir es el texto que se lee.
  const unicas = conDireccion.filter((d, indice) => conDireccion.findIndex((otro) => otro.direccion === d.direccion) === indice);

  if (unicas.length <= 1) return unicas.map((d) => d.direccion);

  return unicas.map((d) => (d.tipo ? `${d.tipo}: ${d.direccion}` : d.direccion));
};

/**
 * Las declaraciones juradas se redactan como oraciones completas, una por renglon, y no como pares
 * "UIF: No": es texto que el representante presta bajo juramento y la redaccion es deliberada.
 *
 * Sigue siendo provisoria: la version definitiva depende de la revision del area legal (TODO
 * abierto en RF-ONB-19).
 */
const armarDeclaraciones = (declaracion?: VistaPrevia['declaracionJurada']) => {
  if (!declaracion) return [];

  return [
    declaracion.esUif === undefined ? '' : declaracion.esUif ? 'La empresa es sujeto obligado ante la UIF.' : 'La empresa no es sujeto obligado ante la UIF.',
    declaracion.esFacta === undefined ? '' : declaracion.esFacta ? 'La empresa está alcanzada por FATCA.' : 'La empresa no está alcanzada por FATCA.',
    declaracion.esOcde === undefined ? '' : declaracion.esOcde ? 'La empresa está alcanzada por OCDE.' : 'La empresa no está alcanzada por OCDE.',
  ].filter(Boolean) as string[];
};

/**
 * Datos de la empresa que el representante esta por ratificar.
 *
 * La identificacion va en una linea unica -razon social, CUIT, domicilio y telefono separados por
 * guiones- y las declaraciones juradas en renglones aparte. Formato pedido por negocio: la version
 * con cada dato en su propia fila quedaba demasiado aireada.
 *
 * Cada tramo sin dato se omite entero en lugar de imprimir un guion: un separador colgando se lee
 * como un error de la pantalla, no como un dato faltante.
 *
 * Sin razon social se devuelve solo el encabezado generico: la vista previa no cargo y no hay nada
 * que ratificar, pero la pantalla no puede quedar muda.
 */
export const armarBloquesEmpresa = (vistaPrevia?: VistaPrevia | null): BloquesEmpresa => {
  const razonSocial = vistaPrevia?.razonSocial?.trim();

  if (!razonSocial) {
    return { encabezado: ENCABEZADO_SIN_DATOS, empresa: null, declaraciones: [], cierre: '' };
  }

  // No se muestra tipoSociedad: el campo guarda un codigo interno, no una descripcion. Es el mismo
  // valor que SendEmailContratoCommand manda como CodTipoSociedad a la integracion de contratos
  // ("AltaSAPJ" y similares), y al representante no le dice nada. Si en algun momento se quiere el
  // tipo de sociedad legible, hay que resolverlo contra el catalogo, no imprimir el codigo.
  const cuit = mostrarCuit(vistaPrevia?.cuit);
  const telefono = mostrarTelefono(vistaPrevia?.telefono);

  const detalle = [
    cuit && `CUIT ${cuit}`,
    ...armarDomicilios(vistaPrevia?.domicilios),
    telefono && `Tel. ${telefono}`,
  ]
    .filter(Boolean)
    .join(' - ');

  return {
    encabezado: 'Estás por validar tu identidad como representante legal para aprobar la creación de una CVU en la empresa:',
    empresa: { razonSocial, detalle },
    declaraciones: armarDeclaraciones(vistaPrevia?.declaracionJurada),
    cierre: CIERRE,
  };
};
